import { Alert, Badge, Button, Card, Form, Spinner } from 'react-bootstrap';
import { listPendingItems } from '../api/financeApi';
import useFinanceQuery from '../useFinanceQuery';
import FinanceSearchBar from '../components/FinanceSearchBar';
import DetailLinkButton from '../components/DetailLinkButton';
import { useDetailModals } from '../components/detailModals';
import { useFinance } from '../financeContext';
import { VERTICALS, VERTICAL_LABEL } from '../wording';
import { ageLabel, severityVariant } from '../format';

// Part B — "Important pending items": deliveries/visits past their due date with nobody assigned,
// bookings not yet accepted, overdue stay follow-ups. The default landing page for a finance user.
const TYPE_LABEL = {
  DELIVERY_UNASSIGNED: 'Delivery — nobody assigned',
  DELIVERY_NEEDS_REASSIGN: 'Delivery — agent declined, needs reassignment',
  VISIT_AGENT_UNASSIGNED: 'Site visit — no agent assigned',
  VISIT_NEEDS_REASSIGN: 'Site visit — agent declined, needs reassignment',
  BOOKING_ACCEPT_OVERDUE: 'Booking — not yet accepted',
  STAY_FOLLOWUP_OVERDUE: 'Stay — follow-up overdue'
};
const SEVERITY_ORDER = { overdue: 0, imminent: 1 };
const TYPE_OPTIONS = Object.keys(TYPE_LABEL);

function isVisitType(t) {
  return t === 'VISIT_AGENT_UNASSIGNED' || t === 'VISIT_NEEDS_REASSIGN';
}

export default function PendingItemsPage() {
  const modals = useDetailModals();
  const { scopedVertical } = useFinance();
  const q = useFinanceQuery(
    (params) => listPendingItems(params),
    { filterKeys: ['vertical', 'type', 'severity'] }
  );

  const rows = [...q.rows].sort((a, b) => {
    const s = (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9);
    if (s) return s;
    return (b.ageHours || 0) - (a.ageHours || 0);
  });

  const groups = {};
  rows.forEach((r) => { (groups[r.type] = groups[r.type] || []).push(r); });

  const openFix = (item) => {
    const entity = item.entity || { _id: item.id };
    if (isVisitType(item.type)) modals.openVisit(entity);
    else modals.openBooking(entity);
  };

  return (
    <div>
      <div className="d-flex flex-wrap align-items-baseline justify-content-between gap-2 mb-2">
        <h5 className="mb-0">Important pending items</h5>
        <span className="small text-muted">{q.pagination?.total ?? rows.length} items</span>
      </div>
      <p className="text-muted small">
        Deliveries and visits past their due date with nobody assigned, bookings still unaccepted,
        and overdue follow-ups — fix them before the delivery or visit.
      </p>

      <div className="d-flex flex-wrap gap-2 mb-3 align-items-end">
        <div style={{ maxWidth: 320, flex: '1 1 240px' }}>
          <FinanceSearchBar value={q.qDraft} onChange={q.setQDraft} placeholder="Search reference or name" />
        </div>
        {!scopedVertical && (
          <Form.Select size="sm" style={{ width: 'auto' }} value={q.get('vertical')} onChange={(e) => q.setFilter('vertical', e.target.value)}>
            <option value="">All verticals</option>
            {VERTICALS.map((v) => <option key={v} value={v}>{VERTICAL_LABEL[v]}</option>)}
          </Form.Select>
        )}
        <Form.Select size="sm" style={{ width: 'auto' }} value={q.get('type')} onChange={(e) => q.setFilter('type', e.target.value)}>
          <option value="">All types</option>
          {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
        </Form.Select>
        <Form.Select size="sm" style={{ width: 'auto' }} value={q.get('severity')} onChange={(e) => q.setFilter('severity', e.target.value)}>
          <option value="">Any severity</option>
          <option value="overdue">Overdue</option>
          <option value="imminent">Imminent</option>
        </Form.Select>
        {q.activeFilterCount > 0 && (
          <Button size="sm" variant="outline-secondary" onClick={q.clearFilters}>Clear</Button>
        )}
      </div>

      {q.error && <Alert variant="danger">{q.error}</Alert>}
      {q.loading ? (
        <Spinner animation="border" size="sm" role="status" />
      ) : rows.length === 0 ? (
        <Alert variant="light">Nothing needs attention right now.</Alert>
      ) : (
        Object.entries(groups).map(([type, items]) => (
          <div key={type} className="mb-4">
            <h6 className="mb-2">
              {TYPE_LABEL[type] || type} <Badge bg="secondary">{items.length}</Badge>
            </h6>
            {items.map((item) => (
              <Card key={item.id || item.ref} className="mb-2">
                <Card.Body className="py-2 px-3 d-flex flex-wrap gap-2 align-items-center justify-content-between">
                  <div style={{ minWidth: 0 }}>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <Badge bg={severityVariant(item.severity)}>{item.severity}</Badge>
                      <span className="fw-semibold">{item.title}</span>
                      <Badge bg="light" text="dark" className="border">{item.vertical}</Badge>
                    </div>
                    {item.subtitle && <div className="small text-muted">{item.subtitle}</div>}
                    <div className="small text-muted">
                      {item.dueAt && <>due {new Date(item.dueAt).toLocaleString()} · </>}
                      {ageLabel(item.ageHours)}
                    </div>
                  </div>
                  <div className="d-flex gap-2 align-items-center">
                    <DetailLinkButton
                      kind={isVisitType(item.type) ? 'visit' : 'booking'}
                      entity={item.entity || { _id: item.id }}
                      label="Details"
                    />
                    <Button size="sm" variant="primary" onClick={() => openFix(item)}>
                      {item.suggestedAction || 'Fix'}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        ))
      )}
    </div>
  );
}
